
import { useState, useEffect } from 'react';
import { parseISO, format, isBefore, isAfter, addDays } from 'date-fns';
import { CalendarEvent } from './Calendar';
import { Bell, MessageSquare, Calendar as CalendarIcon, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { sendSmsReminder } from '@/services/calendarService';
import { useToast } from '@/components/ui/use-toast';

export interface CalendarRemindersListProps {
  events?: CalendarEvent[];
  onEdit: (eventId: string) => void;
  onDelete: (eventId: string) => void;
  onClose?: () => void;
}

export function CalendarRemindersList({ 
  events: propEvents, 
  onEdit, 
  onDelete, 
  onClose 
}: CalendarRemindersListProps) {
  const [activeTab, setActiveTab] = useState<'upcoming' | 'all'>('upcoming');
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const { toast } = useToast();
  const today = new Date();
  
  // Load events from localStorage if not provided as props
  useEffect(() => {
    if (propEvents) {
      setEvents(propEvents);
    } else {
      const savedEvents = localStorage.getItem('calendar-events');
      if (savedEvents) {
        setEvents(JSON.parse(savedEvents));
      }
    }
  }, [propEvents]);
  
  const remindersOnly = events.filter(
    event => event.reminder || event.smsReminder
  );
  
  const upcomingReminders = remindersOnly.filter(event => {
    const eventDate = parseISO(event.date);
    return isAfter(eventDate, today) || isSameDay(eventDate, today);
  });
  
  const sortedUpcomingReminders = [...upcomingReminders].sort((a, b) => {
    return parseISO(a.date).getTime() - parseISO(b.date).getTime();
  });
  
  const sortedAllReminders = [...remindersOnly].sort((a, b) => {
    return parseISO(a.date).getTime() - parseISO(b.date).getTime();
  });
  
  const displayedReminders = activeTab === 'upcoming' 
    ? sortedUpcomingReminders 
    : sortedAllReminders;
    
  const handleSendSmsReminder = async (eventId: string) => {
    try {
      await sendSmsReminder(eventId);
      toast({
        title: "SMS Sent",
        description: "Your reminder SMS has been sent successfully",
      });
    } catch (error) {
      console.error('Error sending SMS reminder:', error);
      toast({
        title: "Error",
        description: "Failed to send SMS reminder",
        variant: "destructive"
      });
    }
  };
  
  // Helper function to check if two dates are the same day
  function isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }
  
  const typeToColorClass = (type: string): string => {
    switch (type) {
      case 'task':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'meeting':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'reminder':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'personal':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };
  
  const isUpcoming = (date: string) => {
    const eventDate = parseISO(date);
    const tomorrow = addDays(today, 1);
    
    if (isSameDay(eventDate, today)) {
      return 'Today';
    } else if (isSameDay(eventDate, tomorrow)) {
      return 'Tomorrow';
    } else if (isAfter(eventDate, tomorrow) && isBefore(eventDate, addDays(today, 7))) {
      return 'This week';
    } else {
      return 'Upcoming';
    }
  };
  
  // Group reminders by date category
  const groupedReminders = displayedReminders.reduce((acc, reminder) => {
    const category = isUpcoming(reminder.date);
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(reminder);
    return acc;
  }, {} as Record<string, CalendarEvent[]>);
  
  // Create an ordered list of categories
  const categories = ['Today', 'Tomorrow', 'This week', 'Upcoming'];
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg md:text-xl font-semibold text-primary">Calendar Reminders</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {remindersOnly.length} reminders
          </span>
          
          {onClose && (
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="h-8 w-8"
              aria-label="Close reminders"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      
      <Tabs defaultValue="upcoming" className="w-full" onValueChange={(value) => setActiveTab(value as 'upcoming' | 'all')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="all">All Reminders</TabsTrigger>
        </TabsList>
        
        <TabsContent value="upcoming" className="mt-4">
          {remindersOnly.length === 0 ? (
            <div className="text-center py-8">
              <CalendarIcon className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
              <p className="mt-4 text-muted-foreground">No calendar reminders scheduled</p>
            </div>
          ) : upcomingReminders.length === 0 ? (
            <div className="text-center py-8">
              <CalendarIcon className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
              <p className="mt-4 text-muted-foreground">No upcoming calendar reminders</p>
            </div>
          ) : (
            <div className="space-y-6">
              {categories.map(category => {
                if (!groupedReminders[category] || groupedReminders[category].length === 0) {
                  return null;
                }
                
                return (
                  <div key={category} className="space-y-3">
                    <h3 className="text-sm font-medium text-muted-foreground">{category}</h3>
                    <div className="space-y-2">
                      {groupedReminders[category].map((reminder) => (
                        <ReminderCard 
                          key={reminder.id} 
                          reminder={reminder} 
                          onEdit={onEdit}
                          onDelete={onDelete}
                          onSendSms={handleSendSmsReminder}
                          colorClass={typeToColorClass(reminder.type)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="all" className="mt-4">
          {remindersOnly.length === 0 ? (
            <div className="text-center py-8">
              <CalendarIcon className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
              <p className="mt-4 text-muted-foreground">No calendar reminders scheduled</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedAllReminders.map((reminder) => (
                <ReminderCard 
                  key={reminder.id} 
                  reminder={reminder} 
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onSendSms={handleSendSmsReminder}
                  colorClass={typeToColorClass(reminder.type)}
                  showPastIndicator={isBefore(parseISO(reminder.date), today) && !isSameDay(parseISO(reminder.date), today)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface ReminderCardProps {
  reminder: CalendarEvent;
  onEdit: (eventId: string) => void;
  onDelete: (eventId: string) => void;
  onSendSms: (eventId: string) => void;
  colorClass: string;
  showPastIndicator?: boolean;
}

function ReminderCard({ 
  reminder, 
  onEdit, 
  onDelete, 
  onSendSms,
  colorClass,
  showPastIndicator = false
}: ReminderCardProps) {
  return (
    <Card className={cn(
      "border overflow-hidden",
      showPastIndicator ? "opacity-60" : ""
    )}>
      <div className={cn("h-1", colorClass.replace('bg-', 'bg-'))} />
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div className="space-y-1">
            <div className="font-medium">{reminder.title}</div>
            <div className="text-xs text-muted-foreground">
              {format(parseISO(reminder.date), 'PPP')}
              {reminder.reminderTime && ` at ${format(new Date(reminder.reminderTime), 'h:mm a')}`}
            </div>
            <div className="flex items-center gap-1 mt-1">
              {reminder.reminder && (
                <Badge variant="outline" className="text-xs flex items-center gap-1 py-0">
                  <Bell className="h-3 w-3" />
                  App
                </Badge>
              )}
              {reminder.smsReminder && (
                <Badge variant="outline" className="text-xs flex items-center gap-1 py-0">
                  <MessageSquare className="h-3 w-3" />
                  SMS
                </Badge>
              )}
              <Badge variant="outline" className={cn("text-xs py-0", colorClass)}>
                {reminder.type}
              </Badge>
              {showPastIndicator && (
                <Badge variant="outline" className="text-xs bg-gray-100 py-0">
                  Past
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 px-2" 
              onClick={() => onEdit(reminder.id)}
            >
              Edit
            </Button>
            {reminder.smsReminder && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 px-2 text-primary" 
                onClick={() => onSendSms(reminder.id)}
              >
                Send SMS
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 px-2 text-destructive" 
              onClick={() => onDelete(reminder.id)}
            >
              Delete
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
